import React, { useState, useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import {
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Box,
  IconButton,
  CircularProgress,
  Switch,
  Chip,
  FormControlLabel,
  Checkbox,
  Collapse,
  InputAdornment,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowBack, Assessment, Add, Delete, Schedule, EventNote } from '@material-ui/icons';
import { getBrandList } from 'api_handlers/brand';
import {
  createCompetitorReportDashboardReport,
  getCombinedBrandListCompetitor,
  getAutoTrackSettings,
} from 'api_handlers/competitor';
import mixpanel from '../../../utils/mixpanel';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#ffffff',
  },
  content: {
    height: '100vh',
    overflow: 'auto',
    flexGrow: 1,
    padding: 0,
    boxSizing: 'border-box',
    backgroundColor: '#f9fafb',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  contentShift: {
    height: '100vh',
    overflow: 'auto',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    backgroundColor: '#f9fafb',
  },
  pageContent: {
    padding: theme.spacing(4),
    margin: '0 auto',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  card: {
    borderLeft: '4px solid #f9a620',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    borderRadius: 8,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  iconContainer: {
    height: 48,
    width: 48,
    borderRadius: theme.shape.borderRadius,
    background: 'linear-gradient(135deg, #f9a620 0%, #548c2f 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    color: '#fff',
    fontSize: 24,
  },
  formSection: {
    marginBottom: theme.spacing(4),
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  platformButtons: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  platformButton: {
    flex: 1,
    textTransform: 'uppercase',
    fontWeight: 600,
    padding: theme.spacing(1.5),
    fontSize: '0.875rem',
    letterSpacing: '0.5px',
    '&.MuiButton-contained': {
      backgroundColor: '#f9a620',
      color: '#fff',
      '&:hover': {
        backgroundColor: '#e6950e',
      },
    },
  },
  dateGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  },
  quickSelectButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  quickSelectButton: {
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  competitorInput: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  competitorField: {
    flex: 1,
  },
  sourceTypeField: {
    width: 170,
    flexShrink: 0,
  },
  generateButton: {
    marginTop: 24,
    padding: '14px 24px',
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: '0.5px',
    fontSize: '0.875rem',
    backgroundColor: '#f9a620',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#e6950e',
    },
  },
  autoTrackCard: {
    borderRadius: 8,
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    marginTop: 24,
    overflow: 'hidden',
  },
  autoTrackCardEnabled: {
    borderLeft: '4px solid #548c2f',
  },
  autoTrackCardDisabled: {
    borderLeft: '4px solid #d1d5db',
  },
  autoTrackIconEnabled: {
    height: 48,
    width: 48,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #548c2f, #2d6a1e)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  },
  autoTrackIconDisabled: {
    height: 48,
    width: 48,
    borderRadius: 8,
    background: '#d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  },
  autoTrackBody: {
    padding: '0 24px 24px',
  },
  scheduleGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  recipientChip: {
    margin: 2,
  },
  recipientInput: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    padding: '8px 12px',
    minHeight: 44,
    backgroundColor: '#fff',
  },
  infoBar: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 8,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: theme.spacing(2),
  },
}));

// Keep in sync with MAX_HASHTAGS_PER_COMPETITOR in the API.
const MAX_HASHTAGS_PER_ROW = 10;

export default function Create() {
  const classes = useStyles();
  const history = useHistory();
  const { id: editId } = useParams();
  const location = useLocation();
  
  // Edit mode when id is present in route
  const isEditMode = Boolean(editId);
  
  // Check if component is loaded in an iframe
  const isInIframe = window.self !== window.top;

  const [brands, setBrands] = useState([]);
  const [reportTitle, setReportTitle] = useState('');
  // Brand ID is resolved from the API in edit mode (editId is now a UUID, not brand_id).
  const [selectedBrand, setSelectedBrand] = useState('');
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [platform, setPlatform] = useState('instagram');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Instagram-specific fields - array of objects
  // source_type: 'handle' (an account), 'hashtag' (posts using the tags) or
  // 'handle_hashtag' (both in one competitor; the tags go in `hashtags`).
  const [instagramDetails, setInstagramDetails] = useState([
    { handle: '', title: '', source_type: 'handle' },
  ]);
  // YouTube-specific fields - array of objects
  const [youtubeDetails, setYoutubeDetails] = useState([{ keyword: '', name: '' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingEditConfig, setLoadingEditConfig] = useState(false);
  // IDs of detail rows that were removed in edit mode — sent to backend for deletion
  const [deletedIds, setDeletedIds] = useState([]);

  const flow = isEditMode ? 'update' : 'create';
  const reportFormData = () => ({
    platform,
    in_iframe: isInIframe,
    competitor_count: platform === 'instagram' ? instagramDetails.length : youtubeDetails.length,
    has_report_title: Boolean((reportTitle || '').trim()),
    start_date: startDate,
    end_date: endDate,
  });
  const trackCompetitorReport = (action, data = {}) => {
    mixpanel.track('competitor_report', { flow, action, data });
  };

  // Auto-Track state
  const [autoTrackEnabled, setAutoTrackEnabled] = useState(false);
  const [autoTrackFrequency, setAutoTrackFrequency] = useState('monthly');
  const [autoTrackDay, setAutoTrackDay] = useState('1');
  const [autoTrackRecipients, setAutoTrackRecipients] = useState([]);
  const [autoTrackEmailInput, setAutoTrackEmailInput] = useState('');
  const [autoTrackPdf, setAutoTrackPdf] = useState(true);
  const [autoTrackDashboardLink, setAutoTrackDashboardLink] = useState(true);

  useEffect(() => {
    loadBrands();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mixpanel.track('competitor_report', {
      flow: isEditMode ? 'update' : 'create',
      action: 'view',
      data: { in_iframe: isInIframe },
    });
  }, [isEditMode, isInIframe]);

  // Prefill form when in edit mode: fetch by group_id (UUID in URL path).
  useEffect(() => {
    if (!isEditMode || !editId) return;

    const query = new URLSearchParams(location.search);
    const platformParam = query.get('platform');
    if (platformParam) setPlatform(platformParam === 'youtube' || platformParam === 'yt' ? 'youtube' : 'instagram');

    let cancelled = false;
    setLoadingEditConfig(true);

    // editId is always the report_group_id UUID in edit mode.
    getCombinedBrandListCompetitor(null, null, null, editId)
      .then((data) => {
        if (cancelled) return;
        const results = data?.results || [];

        // Brand comes from the first combine record's brand FK value.
        const brandId = results?.[0]?.brand ?? null;
        if (brandId) setSelectedBrand(brandId);

        // Populate duration and report_title from API data.
        const startFromData = results?.[0]?.start_date;
        const endFromData = results?.[0]?.end_date;
        if (startFromData) setStartDate(startFromData);
        if (endFromData) setEndDate(endFromData);
        const titleFromData = results?.[0]?.report_title;
        if (titleFromData) setReportTitle(titleFromData);

        if (results.length === 0) return;

        const isYouTube = platformParam === 'youtube' || platformParam === 'yt';
        if (isYouTube) {
          setYoutubeDetails(
            results.map((r) => ({
              id: r.id ?? null,
              keyword: (r.yt_keywords || []).join(', ') || r.yt_handle || r.handle || '',
              name: r.name || r.title || r.yt_handle || '',
              title: r.title || r.name || r.yt_handle || '',
            }))
          );
        } else {
          setInstagramDetails(
            results.map((r) => {
              const sourceType = r.ig_source_type || 'handle';
              const handle =
                (sourceType === 'hashtag'
                  ? r.ig_hashtag
                  : sourceType === 'handle_hashtag'
                    ? r.ig_account_handle
                    : r.ig_handle) ||
                r.handle ||
                '';
              return {
                id: r.id ?? null,
                handle,
                hashtags: sourceType === 'handle_hashtag' ? r.ig_hashtag || '' : '',
                title: r.title || handle,
                source_type: sourceType,
              };
            })
          );
        }

        // Pre-fill auto-track settings from the first combine record.
        const firstCombineId = results?.[0]?.id;
        if (firstCombineId) {
          getAutoTrackSettings(firstCombineId)
            .then((at) => {
              if (cancelled || !at) return;
              setAutoTrackEnabled(at.auto_track_enabled ?? false);
              setAutoTrackFrequency(at.auto_track_frequency || 'monthly');
              setAutoTrackDay(String(at.auto_track_day_of_month ?? 1));
              setAutoTrackRecipients(at.auto_track_recipients || []);
              setAutoTrackPdf(at.auto_track_pdf_attachment ?? true);
              setAutoTrackDashboardLink(at.auto_track_dashboard_link ?? true);
            })
            .catch(() => {});
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Error loading edit config:', err);
      })
      .finally(() => {
        if (!cancelled) setLoadingEditConfig(false);
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editId]);

  // Sync URL with parent window when in iframe
  useEffect(() => {
    if (isInIframe) {
      const syncUrlWithParent = () => {
        const currentPath = history.location.pathname;
        const search = history.location.search;
        window.parent.postMessage({
          type: 'COMPETITOR_REPORT_NAVIGATE',
          path: `/planner${currentPath}${search}`
        }, '*');
      };

      // Sync on mount
      syncUrlWithParent();

      // Listen for history changes
      const unlisten = history.listen((location) => {
        syncUrlWithParent();
      });

      // Listen for browser back/forward buttons
      window.addEventListener('popstate', syncUrlWithParent);

      return () => {
        unlisten();
        window.removeEventListener('popstate', syncUrlWithParent);
      };
    }
  }, [isInIframe, history]);

  const loadBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await getBrandList();
      if (Array.isArray(response)) {
        setBrands(response);
        if (response.length > 0) {
          // In edit mode the brand is set by the prefill effect once API data arrives.
          if (!isEditMode) {
            setSelectedBrand(response[0].id || response[0].name || response[0]);
          }
        }
      }
    } catch (err) {
      console.error('Error loading brands:', err);
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleBack = () => {
    trackCompetitorReport('back', {});
    // editId is the report_group_id UUID — detail URL needs no extra query params.
    const query = new URLSearchParams(location.search);
    const platformParam = query.get('platform');
    let newPath = '/competitor-report-dashboard';
    if (isEditMode && editId) {
      const backQuery = new URLSearchParams();
      if (platformParam) backQuery.set('platform', platformParam);
      newPath = `/competitor-report-dashboard/detail/${editId}${backQuery.toString() ? `?${backQuery.toString()}` : ''}`;
    }
    if (isInIframe) {
      window.parent.postMessage({
        type: 'COMPETITOR_REPORT_NAVIGATE',
        path: `/planner${newPath}`
      }, '*');
    }
    history.push(newPath);
  };

  // Instagram details handlers
  const handleAddInstagramDetail = () => {
    if (instagramDetails.length < 6) {
      setInstagramDetails([
        ...instagramDetails,
        { handle: '', title: '', source_type: 'handle' },
      ]);
    } else {
      alert('You can only add up to 6 Instagram details');
    }
  };

  const handleRemoveInstagramDetail = (index) => {
    if (instagramDetails.length > 1) {
      const removedId = instagramDetails[index]?.id;
      if (removedId) setDeletedIds((prev) => [...prev, removedId]);
      setInstagramDetails(instagramDetails.filter((_, i) => i !== index));
    }
  };

  const extractInstagramHandle = (value) => {
    if (!value) return value;
    const trimmed = value.trim();
    try {
      // Check if it looks like a URL
      if (trimmed.includes('instagram.com')) {
        const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
        // pathname is like /epsonindia or /epsonindia/
        const parts = url.pathname.split('/').filter(Boolean);
        if (parts.length > 0) return parts[0];
      }
    } catch (_) {
      // not a valid URL, fall through
    }
    // Strip leading @ if present
    return trimmed.replace(/^@/, '');
  };

  // A hashtag row can hold several comma-separated tags. Accept pasted tag
  // page URLs (instagram.com/explore/tags/<tag>/) and drop each tag's "#".
  const extractInstagramHashtag = (value) => {
    if (!value) return value;
    return value
      .trimStart()
      .replace(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/explore\/tags\/([^/?#,\s]+)\/?/g, '$1')
      .replace(/(^|[,\s])#+/g, '$1');
  };

  const splitHashtags = (value) =>
    (value || '')
      .split(/[,\s]+/)
      .map((tag) => tag.replace(/^#+/, ''))
      .filter(Boolean);

  const handleInstagramDetailChange = (index, field, value) => {
    const newDetails = [...instagramDetails];
    const detail = { ...newDetails[index] };
    if (field === 'handle') {
      detail.handle =
        detail.source_type === 'hashtag'
          ? extractInstagramHashtag(value)
          : extractInstagramHandle(value);
    } else if (field === 'hashtags') {
      detail.hashtags = extractInstagramHashtag(value);
    } else {
      detail[field] = value;
    }
    newDetails[index] = detail;
    setInstagramDetails(newDetails);
  };

  const handleInstagramSourceTypeChange = (index, sourceType) => {
    const newDetails = [...instagramDetails];
    // A saved row is tied to its competitor brand, so switching type clears
    // the id and creates a new row instead of retyping the old one.
    const { id, ...rest } = newDetails[index];
    if (id) setDeletedIds((prev) => [...prev, id]);
    newDetails[index] = { ...rest, source_type: sourceType, handle: '', hashtags: '' };
    setInstagramDetails(newDetails);
  };

  // YouTube details handlers
  const handleAddYoutubeDetail = () => {
    if (youtubeDetails.length < 6) {
      setYoutubeDetails([...youtubeDetails, { keyword: '', name: '', title: '' }]);
    } else {
      alert('You can only add up to 6 YouTube details');
    }
  };

  const handleRemoveYoutubeDetail = (index) => {
    if (youtubeDetails.length > 1) {
      const removedId = youtubeDetails[index]?.id;
      if (removedId) setDeletedIds((prev) => [...prev, removedId]);
      setYoutubeDetails(youtubeDetails.filter((_, i) => i !== index));
    }
  };

  const handleYoutubeDetailChange = (index, field, value) => {
    const newDetails = [...youtubeDetails];
    newDetails[index][field] = value;
    setYoutubeDetails(newDetails);
  };

  const handleAddRecipient = (e) => {
    if (e.key === 'Enter' && autoTrackEmailInput.trim()) {
      const email = autoTrackEmailInput.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !autoTrackRecipients.includes(email)) {
        setAutoTrackRecipients([...autoTrackRecipients, email]);
      }
      setAutoTrackEmailInput('');
    }
  };

  const handleRemoveRecipient = (email) => {
    setAutoTrackRecipients(autoTrackRecipients.filter((r) => r !== email));
  };

  const getNextRunDate = () => {
    const now = new Date();
    let next = new Date(now);
    const day = parseInt(autoTrackDay, 10) || 1;

    if (autoTrackFrequency === 'weekly') {
      next.setDate(next.getDate() + (7 - next.getDay()) + day);
    } else if (autoTrackFrequency === 'bi-weekly') {
      next.setDate(next.getDate() + 14);
    } else if (autoTrackFrequency === 'monthly') {
      next.setMonth(next.getMonth() + 1);
      next.setDate(Math.min(day, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
    } else if (autoTrackFrequency === 'quarterly') {
      next.setMonth(next.getMonth() + 3);
      next.setDate(Math.min(day, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
    }
    return next.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleQuickDateSelect = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  };

  const handleGenerateReport = async () => {
    // Validation
    if (!selectedBrand) {
      trackCompetitorReport('validation_error', { reason: 'no_brand', ...reportFormData() });
      alert('Please select a brand');
      return;
    }

    // Validate Instagram details - all must be valid
    if (platform === 'instagram') {
      const invalidInstagramDetails = instagramDetails.filter(
        (detail) => !detail.handle.trim() || !detail.title.trim(),
      );
      if (invalidInstagramDetails.length > 0) {
        trackCompetitorReport('validation_error', {
          reason: 'incomplete_instagram',
          invalid_row_count: invalidInstagramDetails.length,
          ...reportFormData(),
        });
        alert('Please fill in all Instagram details (handle or hashtag, and title are required)');
        return;
      }
      // Mirrors the backend: Instagram tags are letters, digits and
      // underscores, and one row tracks at most MAX_HASHTAGS_PER_ROW tags.
      const invalidHashtags = instagramDetails.filter((detail) => {
        if (detail.source_type === 'handle') return false;
        const tags = splitHashtags(
          detail.source_type === 'hashtag' ? detail.handle : detail.hashtags,
        );
        return (
          tags.length === 0 ||
          tags.length > MAX_HASHTAGS_PER_ROW ||
          !tags.every((tag) => /^[\p{L}\p{N}_]+$/u.test(tag))
        );
      });
      if (invalidHashtags.length > 0) {
        trackCompetitorReport('validation_error', {
          reason: 'invalid_hashtag',
          invalid_row_count: invalidHashtags.length,
          ...reportFormData(),
        });
        alert(
          `Separate hashtags with commas (up to ${MAX_HASHTAGS_PER_ROW} per row). ` +
            'Each can only contain letters, numbers and underscores',
        );
        return;
      }
    }

    // Validate YouTube details - all must be valid
    if (platform === 'youtube') {
      const invalidYoutubeDetails = youtubeDetails.filter(
        (detail) => !detail.keyword.trim() || !detail.name.trim() || !detail.title.trim(),
      );
      if (invalidYoutubeDetails.length > 0) {
        trackCompetitorReport('validation_error', {
          reason: 'incomplete_youtube',
          invalid_row_count: invalidYoutubeDetails.length,
          ...reportFormData(),
        });
        alert('Please fill in all YouTube details (keyword, name and title are required)');
        return;
      }
    }

    trackCompetitorReport('save', reportFormData());

    // Auto-commit any email still typed in the input box
    let finalRecipients = autoTrackRecipients;
    if (autoTrackEmailInput.trim()) {
      const pending = autoTrackEmailInput.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pending) && !finalRecipients.includes(pending)) {
        finalRecipients = [...finalRecipients, pending];
        setAutoTrackRecipients(finalRecipients);
      }
      setAutoTrackEmailInput('');
    }

    if (autoTrackEnabled && finalRecipients.length === 0) {
      alert('Please add at least one recipient for Auto-Track');
      return;
    }

    setIsLoading(true);

    const payload = {
      brand: selectedBrand,
      platform: platform,
      start_date: startDate,
      end_date: endDate,
      report_title: reportTitle,
      details: platform === 'instagram' ? instagramDetails : youtubeDetails,
      ...(deletedIds.length > 0 && { deleted_ids: deletedIds }),
      // Pass the existing group UUID so the backend keeps all rows grouped together.
      ...(isEditMode && editId && { group_id: editId }),
      auto_track: {
        enabled: autoTrackEnabled,
        frequency: autoTrackFrequency,
        day_of_month: parseInt(autoTrackDay, 10) || 1,
        recipients: finalRecipients,
        pdf_attachment: autoTrackPdf,
        dashboard_link: autoTrackDashboardLink,
      },
    };

    try {
      const response = await createCompetitorReportDashboardReport(payload);

      if (response && response.message) {
        trackCompetitorReport('success', reportFormData());
        alert(isEditMode ? 'Report updated successfully' : 'Report created successfully');
        // Use report_group_id (UUID) as the stable detail URL — no brand_id or ids needed.
        const groupId = response.report_group_id;
        const detailQuery = new URLSearchParams();
        detailQuery.set('platform', platform);
        if (startDate) detailQuery.set('start_date', startDate);
        if (endDate) detailQuery.set('end_date', endDate);
        const newPath = `/competitor-report-dashboard/detail/${groupId}?${detailQuery.toString()}`;
        if (isInIframe) {
          window.parent.postMessage({
            type: 'COMPETITOR_REPORT_NAVIGATE',
            path: `/planner${newPath}`
          }, '*');
        }
        history.push(newPath);
      } else {
        trackCompetitorReport('failed', { ...reportFormData(), error: 'api' });
        alert(isEditMode ? 'Failed to update report' : 'Failed to create report');
      }
    } catch (err) {
      trackCompetitorReport('exception', { ...reportFormData(), error: 'request' });
      console.error('Competitor report create/update failed:', err);
      alert(isEditMode ? 'Failed to update report' : 'Failed to create report');
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <Box className={classes.root}>
        <Box
          className={`${classes.content}`}
          style={{
            flex: 1,
            transition: 'margin-left 0.3s ease',
            paddingLeft: 0,
          }}
        >
          <Box className={classes.pageContent} style={{ position: 'relative' }}>
            {loadingEditConfig && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="rgba(255,255,255,0.8)"
                zIndex={10}
              >
                <CircularProgress />
              </Box>
            )}
            <Box className={classes.header}>
              <IconButton onClick={handleBack}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h4" style={{ fontWeight: 700 }}>
                  {isEditMode ? 'Edit Competitor Report' : 'Create Competitor Report'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {isEditMode ? 'Update your competitor analysis configuration' : 'Configure your custom competitor analysis'}
                </Typography>
              </Box>
            </Box>

            <Card className={classes.card} style={{ backgroundColor: '#ffffff' }}>
              <CardHeader
                title={
                  <Box className={classes.cardHeader}>
                    <Box className={classes.iconContainer}>
                      <Assessment className={classes.icon} />
                    </Box>
                    <Box>
                      <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Report Configuration
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Set parameters for competitor analysis
                      </Typography>
                    </Box>
                  </Box>
                }
                style={{ paddingBottom: 8 }}
              />
              <CardContent style={{ paddingTop: 0 }}>
                <Box>
                  {/* Your Brand Selection */}
                  <Box className={classes.formSection}>
                    <Typography className={classes.label}>Your Brand</Typography>
                    <Autocomplete
                      options={brands}
                      value={
                        brands.find((b) => {
                          const brandId = b.id ?? b.name ?? b;
                          return String(brandId) === String(selectedBrand) || b === selectedBrand;
                        }) || null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          const brandId = newValue.id ?? newValue.name ?? newValue;
                          setSelectedBrand(brandId);
                        } else {
                          setSelectedBrand('');
                        }
                      }}
                      getOptionLabel={(option) => option.name || option || ''}
                      getOptionSelected={(option, value) => {
                        if (!value) return false;
                        const optionId = option.id ?? option.name ?? option;
                        const valueId = value.id ?? value.name ?? value;
                        return String(optionId) === String(valueId);
                      }}
                      loading={loadingBrands}
                      disabled={loadingBrands}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select your brand"
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingBrands ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box className={classes.formSection}>
                    <Typography className={classes.label}>Report Title</Typography>
                    <TextField
                      value={reportTitle}
                      onChange={(e) => setReportTitle(e.target.value)}
                      placeholder="Report title"
                      fullWidth
                      variant="outlined"
                    />
                  </Box>

                  {/* Platform Selection */}
                  <Box className={classes.formSection}>
                    <Typography className={classes.label}>Platform</Typography>
                    <Box className={classes.platformButtons}>
                      <Button
                        variant={platform === 'instagram' ? 'contained' : 'outlined'}
                        onClick={() => setPlatform('instagram')}
                        className={classes.platformButton}
                      >
                        Instagram
                      </Button>
                      <Button
                        variant={platform === 'youtube' ? 'contained' : 'outlined'}
                        onClick={() => setPlatform('youtube')}
                        className={classes.platformButton}
                      >
                        YouTube
                      </Button>
                    </Box>
                  </Box>

                  {/* Date Range */}
                  <Box className={classes.formSection}>
                    <Typography className={classes.label}>Duration</Typography>
                    <Box className={classes.dateGrid}>
                      <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    </Box>
                    <Box className={classes.quickSelectButtons}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleQuickDateSelect(7)}
                        className={classes.quickSelectButton}
                      >
                        Last 7 Days
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleQuickDateSelect(30)}
                        className={classes.quickSelectButton}
                      >
                        Last 30 Days
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleQuickDateSelect(90)}
                        className={classes.quickSelectButton}
                      >
                        Last 90 Days
                      </Button>
                    </Box>
                  </Box>

                  {/* Instagram-specific fields */}
                  {platform === 'instagram' && (
                    <Box className={classes.formSection}>
                      <Typography className={classes.label}>Instagram Details</Typography>
                      <Box style={{ marginTop: 16 }}>
                        {instagramDetails.map((detail, index) => (
                          <Box key={index} className={classes.competitorInput}>
                            <TextField
                              select
                              label="Type"
                              value={detail.source_type || 'handle'}
                              onChange={(e) =>
                                handleInstagramSourceTypeChange(index, e.target.value)
                              }
                              SelectProps={{ native: true }}
                              variant="outlined"
                              size="small"
                              className={classes.sourceTypeField}
                            >
                              <option value="handle">Handle</option>
                              <option value="hashtag">Hashtag</option>
                              <option value="handle_hashtag">Handle + Hashtag</option>
                            </TextField>
                            <TextField
                              label={detail.source_type === 'hashtag' ? 'Hashtags' : 'Handle'}
                              value={detail.handle}
                              onChange={(e) =>
                                handleInstagramDetailChange(index, 'handle', e.target.value)
                              }
                              placeholder={
                                detail.source_type === 'hashtag' ? 'tag1, tag2, tag3' : 'handle'
                              }
                              InputProps={
                                detail.source_type === 'hashtag'
                                  ? { startAdornment: <InputAdornment position="start">#</InputAdornment> }
                                  : undefined
                              }
                              variant="outlined"
                              size="small"
                              className={classes.competitorField}
                            />
                            {detail.source_type === 'handle_hashtag' && (
                              <TextField
                                label="Hashtags"
                                value={detail.hashtags || ''}
                                onChange={(e) =>
                                  handleInstagramDetailChange(index, 'hashtags', e.target.value)
                                }
                                placeholder="tag1, tag2, tag3"
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">#</InputAdornment>,
                                }}
                                variant="outlined"
                                size="small"
                                className={classes.competitorField}
                              />
                            )}
                            <TextField
                              label="Title"
                              value={detail.title}
                              onChange={(e) =>
                                handleInstagramDetailChange(index, 'title', e.target.value)
                              }
                              placeholder="Report title"
                              variant="outlined"
                              size="small"
                              className={classes.competitorField}
                            />
                            {instagramDetails.length > 1 && (
                              <IconButton
                                onClick={() => handleRemoveInstagramDetail(index)}
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddInstagramDetail}
                        fullWidth
                        style={{
                          marginTop: 8,
                          textTransform: 'uppercase',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                          fontSize: '0.75rem',
                        }}
                      >
                        Add Instagram Detail
                      </Button>
                    </Box>
                  )}

                  {/* YouTube-specific fields */}
                  {platform === 'youtube' && (
                    <Box className={classes.formSection}>
                      <Typography className={classes.label}>YouTube Details</Typography>
                      <Box style={{ marginTop: 16 }}>
                        {youtubeDetails.map((detail, index) => (
                          <Box key={index} className={classes.competitorInput}>
                            <TextField
                              label="Name"
                              value={detail.name}
                              onChange={(e) => {
                                handleYoutubeDetailChange(index, 'name', e.target.value);
                                handleYoutubeDetailChange(index, 'title', e.target.value);
                              }}
                              placeholder="name"
                              variant="outlined"
                              size="small"
                              className={classes.competitorField}
                            />
                            <TextField
                              label="Keyword"
                              value={detail.keyword}
                              onChange={(e) =>
                                handleYoutubeDetailChange(index, 'keyword', e.target.value)
                              }
                              placeholder="Search keyword"
                              variant="outlined"
                              size="small"
                              className={classes.competitorField}
                            />
                            {youtubeDetails.length > 1 && (
                              <IconButton
                                onClick={() => handleRemoveYoutubeDetail(index)}
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddYoutubeDetail}
                        fullWidth
                        style={{
                          marginTop: 8,
                          textTransform: 'uppercase',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                          fontSize: '0.75rem',
                        }}
                      >
                        Add YouTube Detail
                      </Button>
                    </Box>
                  )}

                  {/* Generate Button */}
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleGenerateReport}
                    disabled={isLoading}
                    className={classes.generateButton}
                    startIcon={
                      isLoading ? <CircularProgress size={20} color="inherit" /> : <Assessment />
                    }
                  >
                    {isLoading ? 'Generating Report...' : 'Generate Report'}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Auto-Track Settings Card */}
            <Card
              id="auto-track-card"
              className={`${classes.autoTrackCard} ${
                autoTrackEnabled ? classes.autoTrackCardEnabled : classes.autoTrackCardDisabled
              }`}
              style={{ backgroundColor: '#ffffff' }}
            >
              <CardHeader
                title={
                  <Box className={classes.cardHeader}>
                    <Box
                      className={
                        autoTrackEnabled
                          ? classes.autoTrackIconEnabled
                          : classes.autoTrackIconDisabled
                      }
                    >
                      <Schedule className={classes.icon} />
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Typography variant="h6" style={{ fontWeight: 600, color: autoTrackEnabled ? '#111827' : '#9ca3af' }}>
                        Auto-Track Settings
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {autoTrackEnabled
                          ? 'Automatically fetch data and send reports on a schedule'
                          : 'Enable to automatically fetch data and send reports on a schedule'}
                      </Typography>
                    </Box>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography
                        variant="caption"
                        style={{
                          fontWeight: 600,
                          color: autoTrackEnabled ? '#16a34a' : '#9ca3af',
                        }}
                      >
                        {autoTrackEnabled ? 'Enabled' : 'Disabled'}
                      </Typography>
                      <Switch
                        checked={autoTrackEnabled}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setAutoTrackEnabled(next);
                          if (next) {
                            setTimeout(() => {
                              document.getElementById('auto-track-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 100);
                          }
                        }}
                        size="small"
                        style={{
                          color: autoTrackEnabled ? '#16a34a' : undefined,
                        }}
                      />
                    </Box>
                  </Box>
                }
                style={{ paddingBottom: autoTrackEnabled ? 8 : 16 }}
              />

              <Collapse in={autoTrackEnabled}>
                <Box className={classes.autoTrackBody}>
                  <Box
                    style={{
                      height: 1,
                      background: '#e5e7eb',
                      marginBottom: 20,
                    }}
                  />

                  {/* Schedule */}
                  <Box className={classes.scheduleGrid}>
                    <Box>
                      <Typography
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#374151',
                          marginBottom: 6,
                        }}
                      >
                        Frequency
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={autoTrackFrequency}
                        onChange={(e) => setAutoTrackFrequency(e.target.value)}
                        SelectProps={{ native: true }}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                      </TextField>
                    </Box>
                    <Box>
                      <Typography
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#374151',
                          marginBottom: 6,
                        }}
                      >
                        {autoTrackFrequency === 'weekly' || autoTrackFrequency === 'bi-weekly'
                          ? 'Day of Week'
                          : 'Day of Month'}
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={autoTrackDay}
                        onChange={(e) => setAutoTrackDay(e.target.value)}
                        SelectProps={{ native: true }}
                      >
                        {autoTrackFrequency === 'weekly' ||
                        autoTrackFrequency === 'bi-weekly' ? (
                          <>
                            <option value="1">Monday</option>
                            <option value="2">Tuesday</option>
                            <option value="3">Wednesday</option>
                            <option value="4">Thursday</option>
                            <option value="5">Friday</option>
                          </>
                        ) : (
                          <>
                            <option value="1">1st</option>
                            <option value="5">5th</option>
                            <option value="10">10th</option>
                            <option value="15">15th</option>
                            <option value="20">20th</option>
                            <option value="25">25th</option>
                            <option value="last">Last day</option>
                          </>
                        )}
                      </TextField>
                    </Box>
                  </Box>

                  {/* Recipients */}
                  <Box style={{ marginBottom: 20 }}>
                    <Typography
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: 6,
                      }}
                    >
                      Recipients
                    </Typography>
                    <Box className={classes.recipientInput}>
                      {autoTrackRecipients.map((email) => (
                        <Chip
                          key={email}
                          label={email}
                          size="small"
                          onDelete={() => handleRemoveRecipient(email)}
                          className={classes.recipientChip}
                          style={{
                            backgroundColor: '#eff6ff',
                            color: '#1d4ed8',
                          }}
                        />
                      ))}
                      <input
                        type="email"
                        value={autoTrackEmailInput}
                        onChange={(e) => setAutoTrackEmailInput(e.target.value)}
                        onKeyDown={handleAddRecipient}
                        placeholder="Type email and press Enter..."
                        style={{
                          border: 'none',
                          outline: 'none',
                          fontSize: 13,
                          flex: 1,
                          minWidth: 180,
                          color: '#6b7280',
                          padding: '4px 0',
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Report Format */}
                  <Box style={{ marginBottom: 8 }}>
                    <Typography
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: 2,
                      }}
                    >
                      Report Format
                    </Typography>
                    <Box style={{ display: 'flex', gap: 16 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={autoTrackPdf}
                            onChange={(e) => setAutoTrackPdf(e.target.checked)}
                            size="small"
                            style={{ color: '#548c2f' }}
                          />
                        }
                        label={
                          <Typography style={{ fontSize: 13, color: '#374151' }}>
                            PDF Attachment
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={autoTrackDashboardLink}
                            onChange={(e) => setAutoTrackDashboardLink(e.target.checked)}
                            size="small"
                            style={{ color: '#548c2f' }}
                          />
                        }
                        label={
                          <Typography style={{ fontSize: 13, color: '#374151' }}>
                            Dashboard Link
                          </Typography>
                        }
                      />
                    </Box>
                  </Box>

                  {/* Next Run Info */}
                  <Box className={classes.infoBar}>
                    <EventNote style={{ fontSize: 22, color: '#166534' }} />
                    <Box>
                      <Typography
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#166534',
                        }}
                      >
                        Next scheduled report: {getNextRunDate()}
                      </Typography>
                      <Typography style={{ fontSize: 12, color: '#15803d' }}>
                        Report will be sent to{' '}
                        {autoTrackRecipients.length || 0} recipient
                        {autoTrackRecipients.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Collapse>
            </Card>
          </Box>
        </Box>
      </Box>
  );
}
